import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { toastr } from 'utils';
import { useResetPassword, useSendResetPasswordCode } from 'api/auth/hooks';
import { LOGIN, REGISTER } from 'constants/routes';
import { Form, Wrapper, AuthFooter } from 'components';
import FullPageCardContainer from 'components/FullPageCardContainer/FullPageCardContainer';
import * as Yup from 'yup';

const ForgotPassword = () => {
  const [codeSent, setCodeSent] = useState(false);
  const { t } = useTranslation(['pages', 'app', 'toastr']);
  const navigate = useNavigate();
  const { mutateAsync: sendResetPasswordCode, isPending: isSendingResetPasswordCode } =
    useSendResetPasswordCode();
  const { mutateAsync: resetPassword, isPending: isResettingPassword } = useResetPassword();

  const onSubmitFunc = async (email, reset_password_code, password) => {
    if (!codeSent) {
      try {
        await sendResetPasswordCode({ email });
        setCodeSent(true);
      } catch {
        toastr.error(t('toastr:error'), t('forgotPassword.changePasswordEmailFailure'));
      }

      return;
    }

    try {
      await resetPassword({ reset_password_code, password });
      toastr.success(t('forgotPassword.changePasswordSuccess'));
      navigate(LOGIN);
    } catch (error) {
      toastr.error(
        t('toastr:error'),
        error?.message || t('forgotPassword.changePasswordEmailFailure'),
      );
    }
  };

  const isSubmitting = isSendingResetPasswordCode || isResettingPassword;

  return (
    <FullPageCardContainer
      xs="12"
      md="8"
      lg="6"
      footer={AuthFooter}
      variant="auth"
      cornerAction={{ label: 'Sign up', to: REGISTER }}
    >
      <h1 className="auth-page-title">{t('forgotPassword.title')}</h1>
      <p className="auth-page-subtitle">{t('forgotPassword.subtitle')}</p>
      <Form
        initialValues={{
          email: '',
          reset_password_code: '',
          password: '',
        }}
        onSubmit={(values) =>
          onSubmitFunc(values.email, values.reset_password_code, values.password)
        }
        renderDefaultButtons={false}
        validationSchema={Yup.object({
          email: Yup.string()
            .email(t('validation:invalidEmail'))
            .required(t('validation:required')),
          ...(codeSent && {
            reset_password_code: Yup.string().required(t('validation:required')),
            password: Yup.string().required(t('validation:required')),
          }),
        })}
      >
        {({ values, errors }) => (
          <>
            <Form.Group>
              <Form.Label>{t('app:email')}</Form.Label>
              <Form.Input name="email" type="text" />
            </Form.Group>
            {codeSent && (
              <>
                <Form.Group>
                  <Form.Label>{t('forgotPassword.codeLabel')}</Form.Label>
                  <Form.Input name="reset_password_code" type="text" />
                </Form.Group>
                <Form.Group>
                  <Form.Label>{t('forgotPassword.passwordLabel')}</Form.Label>
                  <Form.Password name="password" placeholder={t('login.passwordPlaceholder')} />
                </Form.Group>
              </>
            )}
            <Wrapper mb-3>
              <Form.Submit
                size="lg"
                variant="primary"
                block
                ignoreClickedState
                disabled={
                  !values.email ||
                  (codeSent && (!values.reset_password_code || !values.password)) ||
                  errors.email ||
                  errors.reset_password_code ||
                  errors.password ||
                  isSubmitting
                }
                isLoading={isSubmitting}
                style={{ width: '100%' }}
              >
                {codeSent ? t('forgotPassword.changePassword') : t('forgotPassword.sendCode')}
              </Form.Submit>
            </Wrapper>
            <Wrapper d-flex flex-column justify-content-center align-items-center mt-4>
              <Link to={LOGIN} className="auth-secondary-link">
                {t('forgotPassword.backToLogin')}
              </Link>
            </Wrapper>
          </>
        )}
      </Form>
    </FullPageCardContainer>
  );
};

export default ForgotPassword;
